import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("session_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Inject the active org_id into list endpoints unless the caller already set one.
  // Skip for endpoints that are global (attendance, payroll, users, search, stats, organizations themselves, expenses, daily-entries).
  try {
    const orgId = localStorage.getItem("active_org_id") || "";
    const url = (config.url || "").toString();
    const isOrgScoped = (
      url.startsWith("/jobs") ||
      url.startsWith("/clients") ||
      url.startsWith("/invoices") ||
      url.startsWith("/invoice-senders") ||
      url.startsWith("/email/accounts")
    );
    if (orgId && isOrgScoped) {
      const method = (config.method || "get").toLowerCase();
      if (method === "get") {
        config.params = { org_id: orgId, ...(config.params || {}) };
      } else if (method === "post" || method === "patch" || method === "put") {
        // Stamp body with org_id when caller hasn't set one
        const body = config.data;
        if (body && typeof body === "object" && !(body instanceof FormData) && !("org_id" in body)) {
          config.data = { ...body, org_id: orgId };
        }
      }
    }
  } catch { /* ignore */ }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      const path = window.location.pathname;
      if (path !== "/login" && !path.startsWith("/auth")) {
        localStorage.removeItem("session_token");
      }
    }
    return Promise.reject(err);
  }
);

export default api;

export const STAGE_LABELS = {
  assigned: "Assigned",
  in_field: "In Field",
  drafter: "In Drafting",
  qa1: "In Checking",
  qa2: "QA/QC 2",
  final_qc: "In Final QC",
  sent_back_to_field: "Sent Back To Field",
  for_revision: "For Revision",
  sent_to_client: "Sent to Client",
  completed: "Completed",
  hold: "Hold",
  cancelled: "Cancelled",
};

export const STAGE_COLORS = {
  assigned: "bg-zinc-200",
  in_field: "bg-amber-200",
  drafter: "bg-blue-200",
  qa1: "bg-yellow-200",
  qa2: "bg-orange-200",
  final_qc: "bg-indigo-200",
  sent_back_to_field: "bg-rose-200",
  for_revision: "bg-pink-200",
  sent_to_client: "bg-green-200",
  completed: "bg-emerald-200",
  hold: "bg-amber-200",
  cancelled: "bg-red-200",
};

export const ROLE_LABELS = {
  admin: "Admin",
  manager: "Team Manager",
  leader: "Team Leader",
  sr_engineer: "Sr. Engineer",
  jr_engineer: "Jr. Engineer",
  drafter: "Drafter",
  qa1: "QA/QC 1",
  qa2: "QA/QC 2",
  final_qc: "Final QC",
  hr_manager: "HR Manager",
  office_assistant: "Office Assistant",
};

// Work stages on which actual time is logged (per-stage tracking)
export const WORK_STAGES = ["drafter", "qa1", "qa2", "final_qc"];

// SLA / planned given-time presets per stage (hours)
// Drafter: 1.0 / 1.5 / 2.0 hr
// QA/QC1 + QA/QC2: locked at 0.5 hr (30 min) — OPT-IN, blank by default so jobs that
// don't go through QA aren't double-charged for stages that never happened.
// Final QC: 0.25 / 0.33 / 0.42 hr (15 / 20 / 25 min)
export const STAGE_GIVEN_OPTIONS = {
  drafter: [1.0, 1.5, 2.0],
  qa1: [0.5],
  qa2: [0.5],
  final_qc: [0.25, 0.33, 0.42],
};

export const STAGE_GIVEN_DEFAULT = {
  drafter: 1.5,
  qa1: null,    // Opt-in — user must explicitly add QA1 time for it to count
  qa2: null,    // Opt-in — same as QA1
  final_qc: 0.33,
};

export const formatHrLabel = (h) => {
  if (h == null) return "—";
  const mins = Math.round(h * 60);
  if (mins < 60) return `${mins} min`;
  if (mins % 60 === 0) return `${mins / 60} hr`;
  return `${(mins / 60).toFixed(2)} hr (${mins} min)`;
};
